import { NavLink, Outlet } from "react-router-dom";
import { Users, MapPin, Package, UserCog, Building2, Monitor, Bot } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/contexts/AuthContext";

const TABS = [
    { to: "/beheer/klanten", label: "Klanten", icon: Users, testid: "beheer-tab-customers" },
    { to: "/beheer/locaties", label: "Locaties", icon: MapPin, testid: "beheer-tab-locations" },
    { to: "/beheer/producten", label: "Producten", icon: Package, testid: "beheer-tab-products" },
    {
        to: "/beheer/scan-learning",
        label: "AI-koppelingen",
        icon: Bot,
        testid: "beheer-tab-scan-learning",
        roles: ["super_admin", "tenant_admin"],
    },
    {
        to: "/beheer/gebruikers",
        label: "Gebruikers",
        icon: UserCog,
        testid: "beheer-tab-users",
        roles: ["tenant_admin"],
    },
    {
        to: "/beheer/tenants",
        label: "Tenants",
        icon: Building2,
        testid: "beheer-tab-tenants",
        roles: ["super_admin"],
    },
];

export default function BeheerLayout() {
    const { user } = useAuth();
    const visible = TABS.filter((t) => !t.roles || t.roles.includes(user?.role));

    return (
        <div className="p-5 sm:p-7 lg:p-10 xl:p-12 max-w-[1480px] mx-auto pb-24 lg:pb-12">
            {/* Mobiele waarschuwing */}
            <div className="lg:hidden bg-amber-50 border border-amber-300 rounded-md p-4 mb-4" data-testid="beheer-mobile-warning">
                <div className="flex items-start gap-3">
                    <Monitor className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-900">
                        <div className="font-semibold mb-1">Beheer werkt het best op desktop</div>
                        <div className="text-xs text-amber-800">
                            Producten, locaties en klanten beheren is geoptimaliseerd voor groter scherm.
                            Open Florefy Flow op je laptop voor de volledige beheer-functies.
                        </div>
                    </div>
                </div>
            </div>
            <PageHeader
                kicker="Beheer"
                title="Beheer & instellingen"
                subtitle="Beheer master-data en gebruikers van je tenant."
                testid="beheer-header"
            />
            <div className="grid grid-cols-12 gap-5 lg:gap-7" data-testid="beheer-layout">
                <aside className="col-span-12 lg:col-span-2" data-testid="beheer-subnav">
                    <nav className="flex lg:block overflow-x-auto lg:overflow-visible gap-1.5 space-y-0 lg:space-y-1.5 bg-white border border-florefy-900/10 rounded-2xl p-2.5 lg:sticky lg:top-6 shadow-[0_10px_30px_rgba(23,61,44,0.05)]">
                        {visible.map(({ to, label, icon: Icon, testid }) => (
                            <NavLink
                                key={to}
                                to={to}
                                end
                                data-testid={testid}
                                className={({ isActive }) =>
                                    `flex items-center gap-2 lg:gap-3 px-3.5 py-2.5 rounded-xl text-xs lg:text-sm shrink-0 whitespace-nowrap transition-all ${
                                        isActive
                                            ? "bg-florefy-900 text-white font-semibold shadow-[0_6px_16px_rgba(23,61,44,0.16)]"
                                            : "text-ink-600 hover:bg-florefy-50 hover:text-florefy-900"
                                    }`
                                }
                            >
                                <Icon aria-hidden="true" className="h-4 w-4 shrink-0" />
                                {label}
                            </NavLink>
                        ))}
                    </nav>
                </aside>
                <section className="col-span-12 lg:col-span-10 min-w-0" data-testid="beheer-content">
                    <Outlet />
                </section>
            </div>
        </div>
    );
}
